import * as React from 'react';
import {
  ArrowRight,
  ChevronRight,
  Lightbulb,
  AlertTriangle,
  Info,
  CheckCircle2,
  CornerDownRight,
  Check,
  Minus,
  Square,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Guide design system — reusable prose, flow-diagram, status-lifecycle and
 * "mock screenshot" primitives used to author tenant-facing guides. Everything
 * is theme-token based (bg-card / text-foreground / …) so it works in light and
 * dark mode without hardcoded colors. Add content with these blocks rather than
 * raw HTML so every track reads consistently.
 */

// ── Prose ────────────────────────────────────────────────────────────────
export function Lead({ children }: { children: React.ReactNode }) {
  return <p className="mb-5 text-[15px] leading-relaxed text-muted-foreground">{children}</p>;
}
export function P({ children }: { children: React.ReactNode }) {
  return <p className="mb-4 text-[13.5px] leading-relaxed text-muted-foreground">{children}</p>;
}
export function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-3 mt-9 scroll-mt-24 text-[16px] font-semibold tracking-tight text-foreground first:mt-0">
      {children}
    </h3>
  );
}
export function UL({ children }: { children: React.ReactNode }) {
  return <ul className="mb-4 ml-1 space-y-1.5 text-[13.5px] leading-relaxed text-muted-foreground">{children}</ul>;
}
export function LI({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2.5">
      <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary/70" />
      <span className="min-w-0">{children}</span>
    </li>
  );
}
export function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11.5px] text-foreground">{children}</code>
  );
}

// ── Callouts ─────────────────────────────────────────────────────────────
type Tone = 'tip' | 'warn' | 'info' | 'success';
const CALLOUT: Record<Tone, { icon: LucideIcon; ring: string; bg: string; fg: string }> = {
  tip: { icon: Lightbulb, ring: 'border-amber-300/40', bg: 'bg-amber-50/50 dark:bg-amber-500/5', fg: 'text-amber-500' },
  warn: { icon: AlertTriangle, ring: 'border-rose-300/40', bg: 'bg-rose-50/50 dark:bg-rose-500/5', fg: 'text-rose-500' },
  info: { icon: Info, ring: 'border-sky-300/40', bg: 'bg-sky-50/50 dark:bg-sky-500/5', fg: 'text-sky-500' },
  success: { icon: CheckCircle2, ring: 'border-emerald-300/40', bg: 'bg-emerald-50/50 dark:bg-emerald-500/5', fg: 'text-emerald-500' },
};
export function Callout({ tone = 'tip', title, children }: { tone?: Tone; title?: string; children: React.ReactNode }) {
  const c = CALLOUT[tone];
  const Icon = c.icon;
  return (
    <div className={cn('my-5 flex gap-3 rounded-xl border px-4 py-3.5 text-[12.5px] leading-relaxed text-foreground', c.ring, c.bg)}>
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', c.fg)} />
      <div className="min-w-0">
        {title && <div className="mb-0.5 font-semibold">{title}</div>}
        <div className="text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

// ── Numbered steps ───────────────────────────────────────────────────────
export function Steps({ children }: { children: React.ReactNode }) {
  const items = React.Children.toArray(children);
  return (
    <ol className="my-5 space-y-0">
      {items.map((child, i) => (
        <li key={i} className="relative flex gap-4 pb-6 last:pb-0">
          {i < items.length - 1 && (
            <span className="absolute left-[15px] top-8 h-[calc(100%-1.5rem)] w-px bg-border" aria-hidden />
          )}
          <span className="z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/12 text-[12px] font-bold text-primary ring-1 ring-inset ring-primary/20">
            {i + 1}
          </span>
          <div className="min-w-0 pt-1">{child}</div>
        </li>
      ))}
    </ol>
  );
}
export function Step({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <>
      <div className="text-[13.5px] font-semibold text-foreground">{title}</div>
      {children && <div className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{children}</div>}
    </>
  );
}

// ── Flow diagram ─────────────────────────────────────────────────────────
export type FlowTone = 'default' | 'accent' | 'success' | 'warn' | 'danger' | 'muted';
const FLOW_TONE: Record<FlowTone, string> = {
  default: 'bg-card ring-border/70 text-foreground',
  accent: 'bg-primary/10 ring-primary/25 text-foreground',
  success: 'bg-emerald-500/10 ring-emerald-500/25 text-foreground',
  warn: 'bg-amber-500/10 ring-amber-500/25 text-foreground',
  danger: 'bg-rose-500/10 ring-rose-500/25 text-foreground',
  muted: 'bg-muted ring-border/60 text-muted-foreground',
};
const FLOW_ICON: Record<FlowTone, string> = {
  default: 'text-muted-foreground',
  accent: 'text-primary',
  success: 'text-emerald-600 dark:text-emerald-400',
  warn: 'text-amber-600 dark:text-amber-400',
  danger: 'text-rose-600 dark:text-rose-400',
  muted: 'text-muted-foreground',
};

export interface FlowStep {
  icon?: LucideIcon;
  title: string;
  sub?: string;
  tone?: FlowTone;
}
export function FlowNode({ icon: Icon, title, sub, tone = 'default', className }: FlowStep & { className?: string }) {
  return (
    <div
      className={cn(
        'flex min-w-[116px] max-w-[180px] flex-col gap-1 rounded-xl px-3.5 py-2.5 ring-1 ring-inset shadow-sm',
        FLOW_TONE[tone],
        className,
      )}
    >
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className={cn('h-4 w-4 shrink-0', FLOW_ICON[tone])} />}
        <span className="text-[12.5px] font-semibold leading-tight">{title}</span>
      </div>
      {sub && <span className="text-[11px] leading-snug text-muted-foreground">{sub}</span>}
    </div>
  );
}
export function FlowArrow({ className }: { className?: string }) {
  return <ArrowRight className={cn('h-4 w-4 shrink-0 self-center text-muted-foreground/60', className)} />;
}
/** Horizontal node chain that wraps gracefully on narrow screens. */
export function Flow({ steps, className }: { steps: FlowStep[]; className?: string }) {
  return (
    <div className={cn('my-5 flex flex-wrap items-stretch gap-x-1.5 gap-y-2 rounded-2xl border border-border/60 bg-muted/20 p-4', className)}>
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <FlowNode {...s} />
          {i < steps.length - 1 && <FlowArrow />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Status lifecycle ─────────────────────────────────────────────────────
export interface LifecycleState {
  label: string;
  tone?: FlowTone;
}
const PILL_TONE: Record<FlowTone, string> = {
  default: 'bg-card ring-border/70 text-foreground',
  accent: 'bg-primary/10 ring-primary/25 text-primary',
  success: 'bg-emerald-500/10 ring-emerald-500/30 text-emerald-700 dark:text-emerald-300',
  warn: 'bg-amber-500/10 ring-amber-500/30 text-amber-700 dark:text-amber-300',
  danger: 'bg-rose-500/10 ring-rose-500/30 text-rose-700 dark:text-rose-300',
  muted: 'bg-muted ring-border/60 text-muted-foreground',
};
function Pill({ label, tone = 'default' }: LifecycleState) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-3 py-1 text-[11.5px] font-semibold ring-1 ring-inset', PILL_TONE[tone])}>
      {label}
    </span>
  );
}
/** Happy-path status chain + optional branch row(s) for exception states. */
export function Lifecycle({
  states,
  branches,
  className,
}: {
  states: LifecycleState[];
  branches?: { from: string; to: LifecycleState[] }[];
  className?: string;
}) {
  return (
    <div className={cn('my-5 rounded-2xl border border-border/60 bg-muted/20 p-4', className)}>
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
        {states.map((s, i) => (
          <React.Fragment key={s.label}>
            <Pill {...s} />
            {i < states.length - 1 && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />}
          </React.Fragment>
        ))}
      </div>
      {branches?.map((b) => (
        <div key={b.from} className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-2 pl-1 text-muted-foreground">
          <CornerDownRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
          <span className="text-[11px] text-muted-foreground">from {b.from}:</span>
          {b.to.map((s) => (
            <Pill key={s.label} {...s} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── "Mock screenshot" figure ─────────────────────────────────────────────
/** A framed illustration that mimics an app screen, with a caption. Use to
 *  show what a screen looks like without bundling real screenshots. */
export function Figure({ label, caption, children }: { label?: string; caption?: string; children: React.ReactNode }) {
  return (
    <figure className="my-5 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className="flex items-center gap-1.5 border-b border-border/70 bg-muted/40 px-3.5 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
        {label && <span className="ml-2 text-[11px] font-medium text-muted-foreground">{label}</span>}
      </div>
      <div className="p-4">{children}</div>
      {caption && (
        <figcaption className="border-t border-border/70 bg-muted/20 px-4 py-2 text-[11.5px] text-muted-foreground">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

// ── Definition / key-value rows ──────────────────────────────────────────
export function DefList({ items }: { items: { term: React.ReactNode; def: React.ReactNode }[] }) {
  return (
    <dl className="my-5 divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60">
      {items.map((it, i) => (
        <div key={i} className="grid gap-1 px-4 py-3 sm:grid-cols-[180px_1fr] sm:gap-4">
          <dt className="text-[12.5px] font-semibold text-foreground">{it.term}</dt>
          <dd className="text-[12.5px] leading-relaxed text-muted-foreground">{it.def}</dd>
        </div>
      ))}
    </dl>
  );
}

// ── Comparison matrix (e.g. role × access) ───────────────────────────────
export interface MatrixRow {
  label: React.ReactNode;
  /** One cell per column: true = yes, false = no, or a string for custom text. */
  cells: (boolean | string)[];
}
export function Matrix({ columns, rows }: { columns: string[]; rows: MatrixRow[] }) {
  return (
    <div className="my-5 overflow-x-auto rounded-xl border border-border/60">
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="border-b border-border/60 bg-muted/40">
            <th className="px-4 py-2.5 text-left font-semibold text-muted-foreground" />
            {columns.map((c) => (
              <th key={c} className="px-3 py-2.5 text-center font-semibold text-foreground">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border/40 last:border-0">
              <td className="px-4 py-2.5 text-foreground">{r.label}</td>
              {r.cells.map((cell, j) => (
                <td key={j} className="px-3 py-2.5 text-center">
                  {cell === true ? (
                    <Check className="mx-auto h-4 w-4 text-emerald-500" />
                  ) : cell === false ? (
                    <Minus className="mx-auto h-3.5 w-3.5 text-muted-foreground/40" />
                  ) : (
                    <span className="text-[11.5px] text-muted-foreground">{cell}</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Checklist (to-do style; optional deep link per item) ──────────────────
export function Checklist({ items }: { items: { label: React.ReactNode; to?: string }[] }) {
  return (
    <ul className="my-5 space-y-2">
      {items.map((it, i) => {
        const body = (
          <>
            <Square className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50 group-hover:text-primary" />
            <span className="min-w-0 text-[13px] text-foreground">{it.label}</span>
            {it.to && <ArrowRight className="ml-auto mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/50 group-hover:text-primary" />}
          </>
        );
        return (
          <li key={i}>
            {it.to ? (
              <Link
                to={it.to}
                className="group flex items-start gap-2.5 rounded-lg border border-border/60 bg-card px-3.5 py-2.5 transition-colors hover:border-primary/30 hover:bg-primary/5"
              >
                {body}
              </Link>
            ) : (
              <div className="flex items-start gap-2.5 rounded-lg border border-border/60 bg-card px-3.5 py-2.5">{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── Code block (multi-line, dark surface) ────────────────────────────────
export function CodeBlock({ language, children }: { language?: string; children: string }) {
  return (
    <div className="my-5 overflow-hidden rounded-xl border border-slate-800 bg-[hsl(222_47%_11%)] shadow-sm">
      {language && (
        <div className="border-b border-slate-800 px-4 py-1.5 text-[10.5px] font-medium uppercase tracking-wide text-slate-400">
          {language}
        </div>
      )}
      <pre className="overflow-x-auto px-4 py-3.5 text-[12px] leading-relaxed text-slate-100">
        <code className="font-mono">{children}</code>
      </pre>
    </div>
  );
}

// ── REST endpoint reference ──────────────────────────────────────────────
const METHOD_TONE: Record<string, string> = {
  GET: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 ring-emerald-500/25',
  POST: 'bg-sky-500/15 text-sky-600 dark:text-sky-400 ring-sky-500/25',
  PATCH: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 ring-amber-500/25',
  PUT: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 ring-amber-500/25',
  DELETE: 'bg-rose-500/15 text-rose-600 dark:text-rose-400 ring-rose-500/25',
};
export interface EndpointDef {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  desc: React.ReactNode;
}
export function Endpoints({ items }: { items: EndpointDef[] }) {
  return (
    <div className="my-5 divide-y divide-border/50 overflow-hidden rounded-xl border border-border/60">
      {items.map((e, i) => (
        <div key={i} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5">
          <span className={cn('inline-flex w-[58px] shrink-0 justify-center rounded-md px-1.5 py-0.5 text-[10.5px] font-bold ring-1 ring-inset', METHOD_TONE[e.method])}>
            {e.method}
          </span>
          <code className="font-mono text-[12px] text-foreground">{e.path}</code>
          <span className="ml-auto text-[11.5px] text-muted-foreground">{e.desc}</span>
        </div>
      ))}
    </div>
  );
}

// ── Chart frame ──────────────────────────────────────────────────────────
export function ChartCard({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="my-5 rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h4 className="text-[13px] font-semibold text-foreground">{title}</h4>
        {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
